import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { trackedObject } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismInput,
  PrismLoadingBar,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t, tKey } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import type { UserLocale } from 'frontend-utils/models/user';
import { LANGUAGES_LIST } from 'frontend-utils/utils/constants/constant-values';
import hasFeature from 'lumen/helpers/has-feature';
import type AjaxService from 'lumen/services/ajax';
import { DepartmentJob } from 'password-form';

type Language = {
  id: string;
  label: string;
};

const DEFAULT_LANGUAGE: Language = {
  id: '',
  label: '',
};

function getUserLanguage(
  locale: string | undefined,
  languagesList: Language[],
): Language {
  if (!locale) {
    return DEFAULT_LANGUAGE;
  }

  let language = DEFAULT_LANGUAGE;

  languagesList.forEach((lang) => {
    if (lang.id === locale) {
      language = lang;
      return;
    }
  });

  return language;
}

// These are the default Django validation errors mapped to our translation keys
const EDIT_USER_ERROR_MESSAGES = {
  'Another user with this email already exists': tKey('user.email_exists'),
};

interface FormUserEditError {
  payload: {
    meta: {
      cause: string;
    };
  };
}

function isFormUserEditError(error: unknown): error is FormUserEditError {
  return (error as FormUserEditError)?.payload?.meta?.cause !== undefined;
}

interface SettingsPersonalFormsFormUserEditSignature {
  Args: {};
}

export default class SettingsPersonalFormsFormUserEdit extends Component<SettingsPersonalFormsFormUserEditSignature> {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare intlLoader: Services['intl-loader'];
  @service declare locale: Services['locale'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked formFields = trackedObject({
    department: '',
    email: '',
    firstName: '',
    jobTitle: '',
    language: DEFAULT_LANGUAGE,
    lastName: '',
  });
  @tracked isLoading = false;

  get availableLanguages(): Language[] {
    return LANGUAGES_LIST;
  }

  get hasChangesToSave(): boolean {
    const { department, firstName, jobTitle, language, lastName } =
      this.formFields;

    const { user } = this.hotelAndMarketService;

    return (
      firstName !== user.firstName ||
      lastName !== user.lastName ||
      language.id !== user.locale ||
      (department !== user.department && this.isJobTitleValid) ||
      (jobTitle !== user.jobTitle && this.isJobTitleValid)
    );
  }

  get isJobTitleValid(): boolean {
    const { jobTitle } = this.formFields;

    return jobTitle !== '' && jobTitle.length <= 50;
  }

  constructor(
    owner: Owner,
    args: SettingsPersonalFormsFormUserEditSignature['Args'],
  ) {
    super(owner, args);

    this.initializeFormFields();
  }

  initializeFormFields(): void {
    const { department, email, firstName, jobTitle, lastName, locale } =
      this.hotelAndMarketService.user;

    const language = getUserLanguage(locale, LANGUAGES_LIST);

    this.formFields = trackedObject({
      department,
      email,
      firstName,
      jobTitle,
      language,
      lastName,
    });
  }

  @action async submitForm(): Promise<void> {
    const { department, firstName, jobTitle, language, lastName } =
      this.formFields;

    const newLocale = language.id as UserLocale;

    const includeLanguage = this.access.hasFeature(
      'user.personal_settings.edit.detail.language',
    );

    const includeName = this.access.hasFeature(
      'user.personal_settings.edit.detail.name',
    );

    const data = {
      user: {
        department,
        first_name: includeName ? firstName : '',
        job_title: jobTitle,
        language: includeLanguage ? newLocale : '',
        last_name: includeName ? lastName : '',
      },
    };

    this.isLoading = true;

    try {
      const { id: userId, locale: oldLocale } = this.hotelAndMarketService.user;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const payload = await this.ajax.patch(`users/${userId}/`, {
        data: JSON.stringify(data),
        contentType: 'application/json',
      });

      this.isLoading = false;

      // This is necessary because ember does not update the store when an ajax request returns a payload
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload(payload);
      this.initializeFormFields();

      await this.intlLoader.load(newLocale);
      this.locale.setLocale(newLocale);

      if (newLocale !== oldLocale) {
        window.location.reload();
        return;
      }

      this.toast.success(this.intl.t('settings.personal.update_success'), {
        showIcon: true,
      });
    } catch (error: unknown) {
      this.isLoading = false;

      const errors = document.createElement('ul');

      if (isFormUserEditError(error) && error.payload && error.payload.meta) {
        const meta = error.payload.meta as { [cause: string]: string };

        Object.keys(meta).forEach((cause) => {
          const key =
            EDIT_USER_ERROR_MESSAGES[
              meta[cause] as keyof typeof EDIT_USER_ERROR_MESSAGES
            ] ?? 'general.error_title';

          errors.innerHTML += `<li>${this.intl.t(key)}</li>`;
        });
      } else {
        errors.innerHTML += `<li>${this.intl.t('general.error_title')}</li>`;
      }

      this.initializeFormFields();

      void this.alerting.showErrorAlert({
        title: this.intl.t('user.update_error'),
        htmlContent: errors,
      });
    }
  }

  @action updateDepartment(department: string | null): void {
    this.formFields.department = department ?? '';
  }

  @action updateFirstName(event: Event): void {
    this.formFields.firstName = (event.target as HTMLInputElement).value;
  }

  @action updateJob(jobTitle: string | null): void {
    this.formFields.jobTitle = jobTitle ?? '';
  }

  @action updateLanguage(language: Language): void {
    this.formFields.language = language;
  }

  @action updateLastName(event: Event): void {
    this.formFields.lastName = (event.target as HTMLInputElement).value;
  }

  <template>
    <div>
      {{#if this.isLoading}}
        <PrismLoadingBar />
      {{/if}}

      <PrismCard class="form-card" data-test-id="form-user-edit">
        <:header as |Header|>
          <Header.title>
            {{t "settings.personal.your_profile"}}
          </Header.title>
        </:header>

        <:content>
          <div class="content grid gap-400">
            {{#if (hasFeature "user.personal_settings.edit.detail.name")}}
              <div class="grid grid-cols-2 gap-400">
                <PrismInput
                  @label={{t "settings.account.first_name"}}
                  @onInput={{this.updateFirstName}}
                  @removePadding={{true}}
                  @value={{this.formFields.firstName}}
                  data-test-id="input-first-name"
                />
                <PrismInput
                  @label={{t "settings.account.last_name"}}
                  @onInput={{this.updateLastName}}
                  @removePadding={{true}}
                  @value={{this.formFields.lastName}}
                  data-test-id="input-last-name"
                />
              </div>
            {{/if}}

            {{#if (hasFeature "user.personal_settings.view.detail.email")}}
              <div class="grid">
                <PrismInput
                  @disabled={{true}}
                  @helpText={{t "settings.email.change"}}
                  @label={{t "settings.account.email"}}
                  @removePadding={{true}}
                  @type="email"
                  @value={{this.formFields.email}}
                  class="w-full"
                  data-test-id="input-email"
                />
              </div>
            {{/if}}

            <DepartmentJob
              @department={{this.formFields.department}}
              @job={{this.formFields.jobTitle}}
              @onUpdateDepartment={{this.updateDepartment}}
              @onUpdateJob={{this.updateJob}}
            />

            {{#if (hasFeature "user.personal_settings.edit.detail.language")}}
              <div class="grid">
                <label class="input-label text-200--regular">
                  {{t "settings.personal.language"}}
                </label>
                <PrismSelectInput
                  @isSecondary={{true}}
                  @onChange={{this.updateLanguage}}
                  @options={{this.availableLanguages}}
                  @value={{this.formFields.language}}
                  data-test-id="input-language"
                />
              </div>
            {{/if}}
          </div>
        </:content>

        <:footer>
          <div class="flex justify-end">
            <PrismButton
              @isDisabled={{or this.isLoading (not this.hasChangesToSave)}}
              @label={{t "general.save"}}
              @onClick={{this.submitForm}}
              data-test-id="button-edit-user"
            />
          </div>
        </:footer>
      </PrismCard>
    </div>
  </template>
}
